"use client";

import {
  spacing,
  fonts,
  fontSizes,
  colors,
} from "@softmax/design-system/tokens.stylex";

import { useCopyAcknowledgement } from "./use-copy-acknowledgement";
import * as stylex from "@stylexjs/stylex";
import { styles } from "./handoff/agent-handoff.styles";
import { Button } from "@softmax/design-system";
import { Fragment } from "react";

type CommandCopyProps = Readonly<{
  command: string;
  onCopy(text: string): Promise<void>;
}>;

export function CommandCopy({ command, onCopy }: CommandCopyProps) {
  const { copied, copy } = useCopyAcknowledgement({ text: command, onCopy });
  return (
    <div {...stylex.props(localStyles.copyContainer)}>
      <Button
        onClick={() => void copy()}
        aria-label={copied ? "Copied command" : "Copy command"}
        title={copied ? "Copied" : "Copy"}
      >
        <span {...stylex.props(localStyles.copyContents)}>
          <span {...stylex.props(localStyles.copyCommand)}>
            {command
              .split(/(?<=[/\\])(?<!:\/)(?<!:\/\/)/)
              .map((part, index) => (
                <Fragment key={index}>
                  {index > 0 ? <wbr /> : null}
                  {part.split(/(\s+)/).map((chunk, wordIndex) =>
                    /\S/.test(chunk) ? (
                      <span key={wordIndex} {...stylex.props(styles.nowrap)}>
                        {chunk}
                      </span>
                    ) : (
                      chunk
                    ),
                  )}
                </Fragment>
              ))}
          </span>
          <span aria-live="polite" {...stylex.props(localStyles.copyLabel)}>
            {copied ? "Copied" : "copy"}
          </span>
        </span>
      </Button>
    </div>
  );
}

const localStyles = stylex.create({
  copyContainer: {
    display: "flex",
    alignSelf: "flex-start",
    minWidth: 0,
    maxWidth: "100%",
  },
  copyContents: {
    display: "flex",
    alignItems: "center",
    flexWrap: { default: "nowrap", "@media (max-width: 480px)": "wrap" },
    gap: spacing.space16,
    minWidth: 0,
    maxWidth: "100%",
    paddingBlock: spacing.space4,
  },
  copyCommand: {
    minWidth: 0,
    flex: 1,
    overflowX: "auto",
    overflowWrap: "normal",
    whiteSpace: "normal",
    textAlign: "left",
    fontFamily: fonts.mono,
    fontSize: fontSizes.reading,
    lineHeight: 1.35,
    fontWeight: 400,
  },
  copyLabel: {
    flexShrink: 0,
    flexBasis: { default: "auto", "@media (max-width: 480px)": "100%" },
    textAlign: "left",
    fontSize: fontSizes.caption,
    color: colors.chartGreen,
  },
});
